import { useCallback, useEffect, useRef, useState } from "react";
import { UploadedFile } from "../types/UploadedFile.type";
import { UploadStatus } from "../types/UploadStatus.type";

interface UseFileUploadStateParams {
	files: UploadedFile[];
	multiple: boolean;
	onChange: (files: UploadedFile[] | null) => void;
}

// Server-assigned ids are only present once a file has actually persisted.
// Mid-upload rows carry a client-generated `uploadId` (a UUID) and no `id`
// at all, so filtering on a truthy, non-zero `id` reliably separates
// "rows the server knows about" from "rows still in flight".
const getServerIds = (files: UploadedFile[]): Set<number> =>
	new Set(files.filter((f) => typeof f.id === "number" && f.id > 0).map((f) => f.id as number));

const setsEqual = (a: Set<number>, b: Set<number>): boolean =>
	a.size === b.size && [...a].every((id) => b.has(id));

// Merge a freshly-hydrated (and already locally-removed-id-filtered) server
// list with whatever the component currently holds locally, so hydration
// never clobbers state the server doesn't know about yet:
//   - a row mid-upload (no `id`) is always kept, and
//   - a row the user just asked to delete (status DELETING) keeps showing
//     as deleting until its own request settles, rather than snapping back
//     to UPLOADED because the stale server payload still lists it.
const mergeWithLocal = (
	incoming: UploadedFile[],
	local: UploadedFile[]
): UploadedFile[] => {
	const localById = new Map(
		local.filter((f) => typeof f.id === "number" && f.id > 0).map((f) => [f.id, f])
	);
	const incomingIds = new Set(incoming.map((f) => f.id));

	const merged = incoming.map((f) => {
		const existing = localById.get(f.id as number);
		if (existing?.status === UploadStatus.DELETING) {
			return existing;
		}
		return f;
	});

	const localOnly = local.filter(
		(f) =>
			!(typeof f.id === "number" && f.id > 0) ||
			(f.status === UploadStatus.DELETING && !incomingIds.has(f.id))
	);

	return [...merged, ...localOnly];
};

export function useFileUploadState({
	multiple,
	onChange,
	files: initialFiles,
}: UseFileUploadStateParams) {
	// ensure files are never null
	const [files, setFiles] = useState<UploadedFile[]>(() => initialFiles ?? []);

	// The server-id set last applied to local state from `initialFiles`.
	const lastHydratedIdsRef = useRef<Set<number>>(getServerIds(initialFiles ?? []));
	// The server-id set last reported to the parent via `onChange`. Used to
	// recognise the parent echoing our own state straight back in.
	const lastEmittedIdsRef = useRef<Set<number>>(getServerIds(initialFiles ?? []));
	// Ids the user has removed locally that a stale ancestor prop must never
	// be allowed to resurrect.
	const locallyRemovedIdsRef = useRef<Set<number>>(new Set());

	// Keyed on the server-id set, not reference identity: several callers
	// rebuild `initialValue` every render, so a new reference may equally be
	// our own state echoed back or a stale prop still listing a deleted file.
	useEffect(() => {
		const incoming = initialFiles ?? [];
		const incomingIds = getServerIds(incoming);

		// Nothing new since the last time we hydrated from this prop.
		if (setsEqual(incomingIds, lastHydratedIdsRef.current)) {
			return;
		}

		// The parent is just echoing back what we last told it — not new data.
		if (setsEqual(incomingIds, lastEmittedIdsRef.current)) {
			lastHydratedIdsRef.current = incomingIds;
			return;
		}

		lastHydratedIdsRef.current = incomingIds;

		const filteredIncoming = incoming.filter(
			(f) => !(typeof f.id === "number" && locallyRemovedIdsRef.current.has(f.id))
		);

		setFiles((prev) => {
			const merged = mergeWithLocal(filteredIncoming, prev);

			// A stale prop, once filtered of locally-removed ids, may collapse
			// back to exactly what's already displayed — don't churn state (or
			// resurrect anything) in that case.
			if (setsEqual(getServerIds(merged), getServerIds(prev))) {
				return prev;
			}

			return merged;
		});
	}, [initialFiles]);

	const addFiles = useCallback(
		(newFiles: UploadedFile[]) => {
			setFiles((prev) => {
				const newState = multiple
					? [...prev, ...newFiles]
					: newFiles.slice(0, 1);
				lastEmittedIdsRef.current = getServerIds(newState);
				onChange?.(newState);
				return newState;
			});
		},
		[multiple, onChange]
	);

	const updateUploadedFile = useCallback(
		(uploadId: string, updatedFile: UploadedFile) => {
			setFiles((prev) => {
				const newState = prev.map((file) => {
					if (file.uploadId === uploadId) {
						return { ...updatedFile };
					}
					return file;
				});
				lastEmittedIdsRef.current = getServerIds(newState);
				onChange?.(newState);
				return newState;
			});
		},
		[onChange]
	);

	const removeFile = useCallback(
		(id: number | string) => {
			setFiles((prev) => {
				const removed = prev.find((f) => {
					if (typeof id === "string") {
						return f.uploadId === id;
					}
					return f.id === id;
				});
				if (removed && typeof removed.id === "number" && removed.id > 0) {
					locallyRemovedIdsRef.current.add(removed.id);
				}

				const newState = prev.filter((f) => {
					if (typeof id === "string") {
						return f.uploadId !== id;
					}
					return f.id !== id;
				});
				lastEmittedIdsRef.current = getServerIds(newState);
				onChange?.(newState);
				return newState;
			});
		},
		[onChange]
	);

	const clearFiles = useCallback(() => {
		setFiles((prev) => {
			prev.forEach((f) => {
				if (typeof f.id === "number" && f.id > 0) {
					locallyRemovedIdsRef.current.add(f.id);
				}
			});
			return [];
		});
		lastEmittedIdsRef.current = new Set();
		onChange?.([]);
	}, [onChange]);

	const replaceFiles = useCallback(
		(newFiles: UploadedFile[]) => {
			// An explicit, authoritative replace (e.g. via the imperative ref) —
			// treat it as a fresh baseline rather than merging.
			locallyRemovedIdsRef.current = new Set();
			lastHydratedIdsRef.current = getServerIds(newFiles);
			lastEmittedIdsRef.current = getServerIds(newFiles);
			setFiles(newFiles);
			onChange?.(newFiles);
		},
		[onChange]
	);

	return {
		files: files,
		addFiles,
		clearFiles,
		removeFile,
		replaceFiles,
		updateUploadedFile,
	};
}
